/**
 * Update Button Component
 *
 * Displays update status in topbar with badge indicator
 * Opens update modal when clicked
 */

import { ArrowDownCircle } from 'lucide-react';
import { useSetModalOpen, useUpdateStatus } from '../stores/update-store';
import { Tooltip } from './tooltip';

export const UpdateButton = () => {
  const status = useUpdateStatus();
  const setModalOpen = useSetModalOpen();

  // Only show button when update is available or downloading/downloaded
  if (status === 'idle' || status === 'checking' || status === 'error') {
    return null;
  }

  const handleClick = () => {
    setModalOpen(true);
  };

  // Button styling based on status
  const getStatusColor = () => {
    switch (status) {
      case 'available':
        return 'text-blue-500 hover:text-blue-600';
      case 'downloading':
        return 'text-yellow-500 hover:text-yellow-600 animate-pulse';
      case 'downloaded':
        return 'text-green-500 hover:text-green-600';
      case 'installing':
        return 'text-purple-500 hover:text-purple-600 animate-pulse';
      default:
        return 'text-foreground/70 hover:text-foreground';
    }
  };

  const getTooltipText = () => {
    switch (status) {
      case 'available':
        return 'Update available';
      case 'downloading':
        return 'Downloading update...';
      case 'downloaded':
        return 'Update ready to install';
      case 'installing':
        return 'Installing update...';
      default:
        return 'Check for updates';
    }
  };

  return (
    <Tooltip content={getTooltipText()} side="bottom" delay={300}>
      <button
        onClick={handleClick}
        className={`relative flex items-center justify-center w-8 h-8 rounded-md transition-colors ${getStatusColor()}`}
        aria-label="Update available"
      >
        <ArrowDownCircle size={20} />

        {/* Badge indicator for available/downloaded updates */}
        {(status === 'available' || status === 'downloaded') && (
          <span className="absolute top-0 right-0 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
        )}
      </button>
    </Tooltip>
  );
};
